import React, { ReactNode } from "react";
import { Button, Typography } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { MATTER_EDITOR_ASSUMPTIONS, MATTER_EDITOR_EXCLUSIONS } from "../matter-editor";
import {
  AssignedResource,
  isNumber,
  isPrice,
  isPriceDiscount,
  Price,
  PriceRange,
  Proposal,
} from "../../data-types";
const { Text, Paragraph } = Typography;
import styles from "./modal.css";
import dayjs from "dayjs";
import { TaxLabel } from "~/gql/graphql";
import { useTaxonomyLabel } from "~/context/taxonomy-context";

const {
  ["non-styled-list"]: nonStyledListClassName,
  ["lower-alpha-list"]: lowerAlphaListClassName,
  ["table-cell-value"]: tableCellValueClassName,
  ["proposal-line-items"]: proposalLineItemsClassName,
  ["header-logo"]: headerLogo,
} = styles as any;

type FeeScheduleProps = {
  feeSchedules: {
    pricingMethodology: string;
    cost: string;
    serviceLevelAgreement: string;
  }[];
};

const EditButton = ({ onClick }: { onClick: () => void }) => (
  <Button icon={<EditOutlined />} type="link" onClick={onClick} />
);
type LabelWithEditLinkProps = {
  text: string;
  onClick: () => void;
};
const LabelWithEditLink = ({ text, onClick }: LabelWithEditLinkProps) => (
  <>
    {text}
    <EditButton onClick={onClick} />
  </>
);
const isLabelWithEditLinkProps = (value: any): value is LabelWithEditLinkProps =>
  value["text"] && value["onClick"];
type RowProps = {
  label: ReactNode | { text: ReactNode; onClickEdit: (tabKey: string) => void };
  value?: string;
  strong?: "label" | "row";
};

const Row = ({ label, value, strong }: RowProps) => (
  <tr>
    <td colSpan={value ? 1 : 2}>
      {isLabelWithEditLinkProps(label) ? (
        <LabelWithEditLink {...label} />
      ) : (
        <Text strong={strong === "label" || strong === "row"}>{label}</Text>
      )}
    </td>
    {value && (
      <td className={tableCellValueClassName}>
        <Text strong={strong === "row"}>{value}</Text>
      </td>
    )}
  </tr>
);

const FeeOptionSchedule = ({ feeSchedules }: FeeScheduleProps) => {
  return (
    <table className={proposalLineItemsClassName}>
      <thead>
        <tr>
          <td>
            <Paragraph strong>Fee option schedule:</Paragraph>
            <Paragraph>Please select one of the following:</Paragraph>
          </td>
        </tr>
      </thead>
      {feeSchedules && feeSchedules.length > 0 && (
        <tbody>
          <table className={proposalLineItemsClassName}>
            <thead>
              <tr>
                <td></td>
                <td>
                  <Text strong>Pricing methodology</Text>
                </td>
                <td>
                  <Text strong>Cost</Text>
                </td>
                <td>
                  <Text strong>Service Level Agreement</Text>
                </td>
              </tr>
            </thead>
            <tbody>
              {feeSchedules.map((fs, i) => {
                return (
                  <tr key={`${i}-${fs.cost}`}>
                    <td>
                      <Text strong>Option {i + 1}</Text>
                    </td>
                    <td>{fs.pricingMethodology}</td>
                    <td>{fs.cost}</td>
                    <td>{fs.serviceLevelAgreement}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </tbody>
      )}
    </table>
  );
};

const SpacerRow = () => (
  <tr>
    <td colSpan={2}>{"\u00A0"}</td>
  </tr>
);

const formatPriceSingleAverage = (value: number | Price | PriceRange) => {
  if (isNumber(value) || isPrice(value)) {
    return formatPrice(value);
  }

  const lower = Math.round(value.lower || 0);
  const upper = Math.round(value.upper || 0);

  if (0 === lower) {
    return formatPrice(upper);
  }
  if (0 === upper) {
    return formatPrice(lower);
  }

  return formatPrice(Math.round(lower + upper / 2));
};

const formatPrice = (value: number | Price | PriceRange) => {
  if (isNumber(value)) {
    const amount = Math.round(value).toLocaleString();
    return `£${amount}`;
  } else if (isPrice(value)) {
    const amount = Math.round(value.amount).toLocaleString();
    return `£${amount}`;
  } else {
    const lower = Math.round(value.lower || 0).toLocaleString();
    const upper = Math.round(value.upper || 0).toLocaleString();
    return `£${lower} to £${upper}`;
  }
};

const HeaderLogo = ({
  src,
  width = "auto",
  height = "60",
  className,
}: {
  src: string;
  width?: string;
  height?: string;
  className?: string;
}): JSX.Element => {
  return <img className={className} width={width} height={height} src={src} />;
};

const HeaderLogoSection = ({ logo }: { logo: string }) => {
  return (
    <Paragraph>
      <HeaderLogo className={headerLogo} src={logo} />
    </Paragraph>
  );
};

const FeeEarnersIntroSection = ({
  assignedResources,
}: {
  assignedResources: AssignedResource[];
}) => {
  const taxonomy = useTaxonomyLabel();
  return (
    <>
      {assignedResources.length > 0 && (
        <>
          <Paragraph strong>{taxonomy.FeeEarner}s:</Paragraph>
          <Paragraph>Lawyers with day-to-day responsibility for this matter:</Paragraph>
          <Paragraph>
            <ul className={nonStyledListClassName}>
              {assignedResources.map((ar, index) => (
                <li key={index.toString()}>
                  {ar.feeEarnerName
                    ? `${ar.feeEarnerName} (${ar.feeEarnerLevelDescription}) `
                    : `${ar.feeEarnerLevelDescription} `}
                </li>
              ))}
            </ul>
          </Paragraph>
        </>
      )}
    </>
  );
};

const FeeOptionsExplanatoryNotes = ({
  assignedResources,
  taxLabel,
}: {
  assignedResources: AssignedResource[];
  taxLabel: TaxLabel;
}) => {
  return (
    <>
      <Paragraph strong>Fee options – Explanatory notes</Paragraph>
      <Paragraph strong>Hourly rates:</Paragraph>
      {assignedResources.length > 0 && (
        <>
          <Paragraph>
            <ul className={nonStyledListClassName}>
              {assignedResources.map((ar, index) => (
                <li key={index.toString()}>
                  {ar.feeEarnerName
                    ? `${ar.feeEarnerName} (${ar.feeEarnerLevelDescription}) `
                    : `${ar.feeEarnerLevelDescription} `}
                  {" - "}
                  {formatPrice(ar.rate)}
                </li>
              ))}
            </ul>
          </Paragraph>
          <Paragraph>
            Hourly rates are usedto calculate the fees payable based on the time spent on your
            matter. A written estimate is provided but this estimate may have to be revised upwards
            at any time if the amount of work required increases. Hours are divided into tenths for
            charging purposes.
          </Paragraph>
          <Paragraph strong>Advantages:</Paragraph>
          <ul>
            <li>
              If the project proceeds smoothly and without any departure from the agreed scope,
              exclusions and assumptions, the final cost may becheaper than other pricing
              methodologies.
            </li>
            <li>
              You have the ability to monitor cost issues on an ongoing basis and refine your
              instructions in response to those developments. If your work covers more than one
              month, then monthly invoices spread the cost evenly over the life of the project.
            </li>
            <li>
              Our fee will not exceed the actual billable timecalculated by reference to the time
              spent working on your case.
            </li>
          </ul>
          <Paragraph strong>Risks:</Paragraph>
          <ul>
            <li>
              The fundamental issue for you is whether you want to assume and manage the price risk
              yourself (taking the benefit of any potential savingsif the work takes less time than
              estimated,but running the risk that the cost estimate increases as the work develops)
              or whether you prefer us to assume that price risk entirely, giving you a fixed price;
            </li>
            <li>
              There is cost uncertainty for you. The final cost of the project may bemore expensive
              than other pricing options;
            </li>
            <li>There may be lessrelativity between the total fee and the result you achieve;</li>
            <li>
              The range provided is our best estimate based on current information, but wecannot
              rule out the possibility of the fee being higher than the top end of the range.
            </li>
          </ul>
          <Paragraph strong>Hourly rates:</Paragraph>
          <Paragraph strong>Advantages:</Paragraph>
          <ul>
            <li>
              You have complete budgetary predictability and certainty within the scope, assumptions
              and exclusions;
            </li>
            <li>There will never be any pricing surprises. Most of the cost risk sits with us.</li>
            <li>
              If there is a major expansion of workrequiredbut it is one that is still
              withinthescope, assumptions and exclusions, then we have to absorb the additional cost
              and will not pass it on to you.
            </li>
          </ul>
          <Paragraph strong>Risks:</Paragraph>
          <ul>
            <li>
              This option does not completely remove all of the cost risk for youas something may
              arise which is outside the scope or which is covered by an exclusion;
            </li>
            <li>
              If there is a departure from the scope, assumptions and exclusions for any reason, it
              is likely that our price will need to be adjusted upwards. However, we undertake to
              discuss it with you and agree the cost as soon as we become aware of the possibility
              and before incurring any unanticipated cost.
            </li>
          </ul>
          <Paragraph>
            Under a fixed fee arrangement, you might end up paying more than you might have under an
            hourly billing or any other arrangement. Having selected the fixed fee option, that fee
            is payable regardless of the amount of work undertaken.You are however protected from
            all other fee increases, so could also end up paying lessthan would otherwise have
            beenthe case.
          </Paragraph>
          <Paragraph strong>Service Level Guarantee:</Paragraph>
          <Paragraph>
            We know that we can provide you with an exceptional level of client service. Exceptional
            client service consists of things like our availability, prompt phone and email
            responses, unequivocal, timely and commercially pragmatic advice, clear explanations,
            clear written communication, courtesy and ease of doing business withus. We aim to keep
            it simple.
          </Paragraph>
          <Paragraph strong>
            In order to give this guarantee credibility, if you don’t consider that you have had
            exceptional service from us during the course of this engagement, you may
            unilaterallyreduce our fee by up to 10% of the monthly invoice for that period, or the
            fixed fee instalment due at that time -no strings attached.
          </Paragraph>
          <Paragraph>
            All we ask is that if you elect to invoke the guarantee, you do so in a principled and
            fair manner andthat you share with us the ways in which we need to improve our service
            levels. You should note that this is a serviceguarantee, not a resultsor
            outcomeguarantee.
          </Paragraph>
          <Paragraph strong>
            Additional Office Expenses &amp; Payments to Third Parties (called disbursements):
          </Paragraph>
          <Paragraph>
            If you select any one of the hourly rate pricing options, we will make additional
            charges for:
          </Paragraph>
          <ul>
            <li>travel and subsistence expenses;</li>
            <li>transaction fees (including bank fees);</li>
            <li>courier fees;</li>
            <li>external bulk copying and document production; and</li>
            <li>
              other similar expenses necessarily incurred by the firm with third party providers
            </li>
          </ul>
          <Paragraph>
            If you select the fixed price options, we will absorb all these overhead expenses, and
            there will be no additional charge to you.
          </Paragraph>
          <Paragraph>
            Any payments due to third parties such as barristers, mediators, experts, probate
            registryetc are excluded from both the hourly rate and fixed fee optionsbut will not be
            incurred without being agreed with you in advance
          </Paragraph>
          <Paragraph strong>{taxLabel}:</Paragraph>
          <Paragraph>
            All of the foregoing figures are exclusive of {taxLabel} which will be applied at the
            relevant rate.
          </Paragraph>
          <Paragraph
            style={{
              textAlign: "right",
            }}
          >
            {dayjs().format("DD/MM/YYYY")}
          </Paragraph>
        </>
      )}
    </>
  );
};

const ClientSection = ({ clientName }: { clientName: string }) => {
  return (
    <>
      <Paragraph strong>Client:</Paragraph>
      <Paragraph>{clientName}</Paragraph>
    </>
  );
};

const TimetableSection = () => {
  return (
    <>
      <Paragraph strong>Timetable of work:</Paragraph>
      <Paragraph>
        We aim to have the caveat entered within 3 working days of receiving your instructions to do
        so.
      </Paragraph>
      <Paragraph>
        We aim to have the appearance entered within 3 working days of receiving your instructions
        to do so or within 14 days of service of warning if earlier if we advise that entry of
        appearance is appropriate.
      </Paragraph>
    </>
  );
};

const PricingPreambleSection = ({ taxLabel }: { taxLabel: TaxLabel }) => {
  return (
    <>
      <Paragraph strong>Pricing:</Paragraph>
      <Paragraph>
        Please see the attached fee option schedule, which includes traditional hourly ratesorfixed
        fees as well as a service level guarantee. We are happy to work to on eitherof these
        alternatives and the choice is put forward to give you more control over the financial
        aspects of this project.
      </Paragraph>
      <Paragraph>{taxLabel} is charged at 20% in addition to all fees quoted.</Paragraph>
    </>
  );
};

const LineItemsBreakdownSection = ({
  proposal,
}: {
  onClickEdit: (matterEditorChild: string) => void;
  proposal: Pick<
    Proposal,
    | "legalFeeTotal"
    | "legalFeeLineItems"
    | "legalFeeDiscount"
    | "disbursementsTotal"
    | "disbursementLineItems"
    | "disbursementsTax"
    | "legalFeeTax"
    | "grandTotal"
  >;
}) => {
  const { legalFeeLineItems, legalFeeDiscount } = proposal;

  return (
    <>
      <Paragraph strong>Summary of work required:</Paragraph>
      <Paragraph>
        <em>Note:</em> where necessary, figures are rounded to the nearest £.
      </Paragraph>
      <table className={proposalLineItemsClassName}>
        <tbody>
          {legalFeeLineItems.map(({ title, tasks = [] }, index) => {
            const lineItemData = (
              <>
                <Paragraph>{title}</Paragraph>
                {tasks.length > 0 && (
                  <ul>
                    {tasks?.map((t) => (
                      <li key={t.id}>{t.title}</li>
                    ))}
                  </ul>
                )}
              </>
            );
            return <Row key={index.toString()} label={lineItemData} />;
          })}
        </tbody>
      </table>
      <FeeOptionSchedule
        feeSchedules={[
          {
            pricingMethodology: "Hourly rates",
            cost: formatPrice(
              isPriceDiscount(legalFeeDiscount)
                ? legalFeeDiscount.discountedAmount
                : legalFeeDiscount.discountedAmountRange
            ),
            serviceLevelAgreement: "Yes - 10%",
          },
          {
            pricingMethodology: "Fixed fee (a quote)",
            cost: formatPriceSingleAverage(
              isPriceDiscount(legalFeeDiscount)
                ? legalFeeDiscount.discountedAmount
                : legalFeeDiscount.discountedAmountRange
            ),
            serviceLevelAgreement: "Yes - 10%",
          },
        ]}
      />
    </>
  );
};

const AssumptionsAndExclusionsSection = ({
  onClickEdit,
  assumptions,
  exclusions,
}: {
  onClickEdit: (matterEditorChild: string) => void;
  assumptions: string[];
  exclusions: string[];
}) => {
  return (
    <>
      <Paragraph strong>Assumptions &amp; Exclusions:</Paragraph>
      <Paragraph>
        It is important for you to appreciate that the information provided is based on a number of
        assumptions:
      </Paragraph>
      <Paragraph>
        <Text strong>ASSUMPTIONS</Text>
        <EditButton onClick={() => onClickEdit(MATTER_EDITOR_ASSUMPTIONS)} />
        <ol className={lowerAlphaListClassName}>
          {assumptions.map((a, index) => (
            <li key={index.toString()}>{a}</li>
          ))}
        </ol>
      </Paragraph>
      <Paragraph>
        <Text strong>EXCLUSIONS</Text>
        <EditButton onClick={() => onClickEdit(MATTER_EDITOR_EXCLUSIONS)} />
        <ol className={lowerAlphaListClassName}>
          {exclusions.map((e, index) => (
            <li key={index.toString()}>{e}</li>
          ))}
        </ol>
      </Paragraph>
    </>
  );
};

export const ProposalExportPreviewModalBody = ({
  logo,
  proposal,
  clientName,
  onClickEdit,
}: {
  logo: string;
  proposal: Proposal;
  clientName?: string;
  onClickEdit: (matterEditorChild: string) => void;
}) => {
  const { assignedResources, assumptions, exclusions } = proposal;

  return (
    <div>
      <HeaderLogoSection logo={logo} />
      <FeeEarnersIntroSection assignedResources={assignedResources} />
      {clientName && <ClientSection clientName={clientName} />}
      <TimetableSection />
      <PricingPreambleSection taxLabel={TaxLabel.Vat} />
      <AssumptionsAndExclusionsSection
        onClickEdit={onClickEdit}
        assumptions={assumptions}
        exclusions={exclusions}
      />
      <LineItemsBreakdownSection onClickEdit={onClickEdit} proposal={proposal} />
      <SpacerRow />
      <FeeOptionsExplanatoryNotes assignedResources={assignedResources} taxLabel={TaxLabel.Vat} />
    </div>
  );
};
